import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileImage, Image as ImageIcon, RefreshCw, Save, Target } from "lucide-react";

import { PageContent } from "@/shared/widgets/layout/PageContent";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Switch } from "@/shared/ui/switch";
import { useToast } from "@/shared/hooks/use-toast";
import { useTranslation } from "@/shared/i18n";

import { PsdDropzone } from "../components/PsdDropzone";
import { LayerRow } from "../components/LayerRow";
import { PreviewCanvas } from "../components/PreviewCanvas";
import { SlotsPanel } from "../components/SlotsPanel";
import { PreviewImagePanel } from "../components/PreviewImagePanel";
import { SaveProgress } from "../components/SaveProgress";
import { StatusBadge } from "../components/StatusBadge";
import { CategoriesPanel } from "../components/CategoriesPanel";
import { parsePsd } from "../lib/parsePsd";
import { capturePreview } from "../lib/renderTemplate";
import { convertImageToSlot, getSlotLayers, nextSlotId } from "../lib/slots";
import { slugifyId } from "../lib/slugify";
import { fileToWebpBlobCapped, webpOrPng } from "../lib/webp";
import { hydrateBackendTemplate } from "../lib/hydrate";
import { useTemplate, useSaveTemplate } from "../model";
import type { TemplateNameTranslation, TemplateStatus } from "../api";
import type { ParsedPsd, Template, TemplateLayer } from "../types";

const DEFAULT_PREVIEW_FILE_NAME = `template.${webpOrPng().ext}`;

function previewNameForBlob(blob: Blob): string {
  if (blob.type === "image/webp") return "template.webp";
  if (blob.type === "image/jpeg") return "template.jpg";
  return "template.png";
}

function useCanvasMaxHeight() {
  const [h, setH] = useState(() =>
    typeof window === "undefined" ? 600 : Math.round(window.innerHeight * 0.7),
  );
  useEffect(() => {
    const onResize = () => setH(Math.round(window.innerHeight * 0.7));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return h;
}

interface Props {
  mode: "new" | "edit";
}

export default function TemplateEditorPage({ mode }: Props) {
  const { t } = useTranslation("psdTemplates");
  const { toast } = useToast();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const templateId = mode === "edit" ? params.id : undefined;
  const canvasMaxHeight = useCanvasMaxHeight();

  const [parsed, setParsed] = useState<ParsedPsd | null>(null);
  const [template, setTemplate] = useState<Template | null>(null);
  const [parsing, setParsing] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [status, setStatus] = useState<TemplateStatus>("DRAFT");
  const [freshAssets, setFreshAssets] = useState<Set<string>>(new Set());
  const [backendId, setBackendId] = useState<string | undefined>(templateId);
  const [activeZ, setActiveZ] = useState<number | null>(null);
  const [slotOverrideImages, setSlotOverrideImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [slotOverrideUrls, setSlotOverrideUrls] = useState<Map<string, string>>(new Map());
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [nameEn, setNameEn] = useState("");
  const [isFree, setIsFree] = useState(false);

  const { template: backendRecord, isLoading: loadingBackend } = useTemplate(templateId);
  const { save, step, isSaving } = useSaveTemplate();

  const clearOverrides = useCallback(() => {
    setSlotOverrideUrls((curr) => {
      for (const url of curr.values()) URL.revokeObjectURL(url);
      return new Map();
    });
    setSlotOverrideImages(new Map());
  }, []);

  const clearPreview = useCallback(() => {
    setPreviewUrl((curr) => {
      if (curr) URL.revokeObjectURL(curr);
      return null;
    });
    setPreviewBlob(null);
  }, []);

  // Hydrate from backend in edit mode
  useEffect(() => {
    if (mode !== "edit" || !backendRecord) return;
    setStatus(backendRecord.status);
    setBackendId(backendRecord.id);
    const enTranslation = backendRecord.nameTranslations?.find(
      (t) => t.languageCode === "EN",
    );
    setNameEn(enTranslation?.name ?? "");
    setIsFree(backendRecord.isFree ?? false);
    const config = backendRecord.configJson as Template;
    if (!config || !Array.isArray(config.layers) || config.layers.length === 0) {
      // DRAFT without JSON yet — user needs to load a PSD
      setParsed(null);
      setTemplate(null);
      clearOverrides();
      clearPreview();
      return;
    }
    let cancelled = false;
    setHydrating(true);
    hydrateBackendTemplate(config)
      .then((result) => {
        if (cancelled) return;
        setParsed(result.parsed);
        setTemplate(result.parsed.template);
        // Nothing was changed yet — all assets already live in Supabase.
        setFreshAssets(new Set());
        if (result.previewBlob) {
          setPreviewBlob(result.previewBlob);
          setPreviewUrl(URL.createObjectURL(result.previewBlob));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast({
          title: t("toast.hydrateFailed"),
          description: err instanceof Error ? err.message : String(err),
          variant: "destructive",
        });
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, backendRecord, clearOverrides, clearPreview, t, toast]);

  const handleFile = useCallback(
    async (file: File) => {
      setParsing(true);
      try {
        const result = await parsePsd(file);
        setParsed(result);
        setTemplate(result.template);
        // Every asset that came from the PSD is brand new and must be uploaded.
        setFreshAssets(new Set(result.images.keys()));
        clearOverrides();
        clearPreview();
        setActiveZ(null);
        toast({
          title: t("toast.parsedTitle"),
          description: t("toast.parsedDescription", { count: result.template.layers.length }),
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        toast({ title: t("toast.parseFailed"), description: message, variant: "destructive" });
      } finally {
        setParsing(false);
      }
    },
    [clearOverrides, clearPreview, t, toast],
  );

  const handleReset = () => {
    if (mode === "edit") {
      navigate("/psd-templates");
      return;
    }
    setParsed(null);
    setTemplate(null);
    setActiveZ(null);
    setFreshAssets(new Set());
    clearOverrides();
    clearPreview();
  };

  const handleSlotUpload = (slotId: string, file: File) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setSlotOverrideImages((curr) => {
        const next = new Map(curr);
        next.set(slotId, img);
        return next;
      });
    };
    img.src = url;
    setSlotOverrideUrls((curr) => {
      const next = new Map(curr);
      const prev = next.get(slotId);
      if (prev) URL.revokeObjectURL(prev);
      next.set(slotId, url);
      return next;
    });
  };

  const handleSlotClear = (slotId: string) => {
    setSlotOverrideUrls((curr) => {
      const next = new Map(curr);
      const prev = next.get(slotId);
      if (prev) URL.revokeObjectURL(prev);
      next.delete(slotId);
      return next;
    });
    setSlotOverrideImages((curr) => {
      const next = new Map(curr);
      next.delete(slotId);
      return next;
    });
  };

  useEffect(() => {
    return () => {
      setSlotOverrideUrls((curr) => {
        for (const url of curr.values()) URL.revokeObjectURL(url);
        return new Map();
      });
      setPreviewUrl((curr) => {
        if (curr) URL.revokeObjectURL(curr);
        return null;
      });
    };
  }, []);

  const handleToggleSlot = useCallback(
    (z: number) => {
      if (!parsed || !template) return;
      const current = template.layers.find((l) => l.z === z);
      if (!current) return;
      let newLayer: TemplateLayer | null = null;
      if (current.type === "image") {
        const id = nextSlotId(template);
        newLayer = convertImageToSlot(current, id);
      } else if (current.type === "slot") {
        const original = parsed.template.layers.find((l) => l.z === z);
        if (original) newLayer = original;
      }
      if (!newLayer) return;
      setTemplate({
        ...template,
        layers: template.layers.map((l) => (l.z === z ? newLayer! : l)),
      });
    },
    [parsed, template],
  );

  const handleNameChange = (name: string) => {
    setTemplate((curr) => (curr ? { ...curr, name, id: slugifyId(name) } : curr));
  };

  const setPreview = useCallback((blob: Blob) => {
    setPreviewBlob(blob);
    setPreviewUrl((curr) => {
      if (curr) URL.revokeObjectURL(curr);
      return URL.createObjectURL(blob);
    });
    setFreshAssets((curr) => {
      const next = new Set(curr);
      next.add(previewNameForBlob(blob));
      return next;
    });
  }, []);

  const handleCapturePreview = useCallback(async () => {
    if (!template || !parsed) return;
    setCapturing(true);
    try {
      const blob = await capturePreview(template, parsed.canvases, slotOverrideImages);
      setPreview(blob);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: t("toast.exportFailed"), description: message, variant: "destructive" });
    } finally {
      setCapturing(false);
    }
  }, [parsed, template, slotOverrideImages, setPreview, t, toast]);

  const handlePreviewUpload = async (file: File) => {
    const webp = await fileToWebpBlobCapped(file);
    setPreview(webp);
  };

  const previewFileName = useMemo(() => {
    if (previewBlob) return previewNameForBlob(previewBlob);
    return template?.preview ?? DEFAULT_PREVIEW_FILE_NAME;
  }, [previewBlob, template?.preview]);

  const slotLayers = useMemo(() => (template ? getSlotLayers(template) : []), [template]);
  const uniqueSlotCount = useMemo(() => new Set(slotLayers.map((s) => s.id)).size, [slotLayers]);

  const handleSave = async () => {
    if (!template || !parsed) return;
    if (!previewBlob) {
      toast({
        title: t("toast.previewRequiredTitle"),
        description: t("toast.previewRequiredDescription"),
        variant: "destructive",
      });
      return;
    }
    const hasCategory = (backendRecord?.categories?.length ?? 0) > 0;
    if (backendId && !hasCategory) {
      toast({
        title: t("toast.categoryRequiredTitle"),
        description: t("toast.categoryRequiredDescription"),
        variant: "destructive",
      });
      return;
    }
    try {
      const finalTemplate: Template = { ...template, preview: previewFileName };
      const nameTranslations: TemplateNameTranslation[] = [
        { languageCode: "PT_BR", name: template.name },
      ];
      const trimmedEn = nameEn.trim();
      if (trimmedEn) {
        nameTranslations.push({ languageCode: "EN", name: trimmedEn });
      }
      const saved = await save({
        existingId: backendId,
        template: finalTemplate,
        nameTranslations,
        preview: previewFileName,
        images: parsed.images,
        previewBlob,
        previewFileName,
        freshAssets,
        isFree,
      });
      setBackendId(saved.id);
      setStatus(saved.status);
      setFreshAssets(new Set());
      if (mode === "new") {
        navigate(`/psd-templates/${saved.id}`, { replace: true });
      }
    } catch {
      // toast handled inside hook
    }
  };

  const busy = parsing || hydrating || isSaving;
  const showLoading = mode === "edit" && (loadingBackend || hydrating);

  const breadcrumb = [
    { label: t("pageTitle"), href: "/psd-templates" },
    { label: mode === "new" ? t("editor.new") : template?.name ?? "...", href: "" },
  ];

  return (
    <PageContent
      title={mode === "new" ? t("editor.new") : template?.name ?? t("pageTitle")}
      items={breadcrumb}
      actions={
        <div className="flex items-center gap-2">
          {mode === "edit" && <StatusBadge status={status} />}
          <Button variant="ghost" onClick={() => navigate("/psd-templates")} disabled={busy}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("editor.back")}
          </Button>
          {template && (
            <>
              <Button variant="outline" onClick={handleReset} disabled={busy}>
                <RefreshCw className="w-4 h-4 mr-2" />
                {mode === "new" ? t("actions.reset") : t("editor.discard")}
              </Button>
              <Button onClick={handleSave} disabled={busy}>
                <Save className="w-4 h-4 mr-2" />
                {isSaving ? t("save.saving") : t("editor.save")}
              </Button>
            </>
          )}
        </div>
      }
    >
      {showLoading ? (
        <div className="border rounded-xl p-12 text-center text-muted-foreground">
          {t("editor.loading")}
        </div>
      ) : !template ? (
        <div className="space-y-4">
          {mode === "edit" && (
            <p className="text-sm text-muted-foreground">{t("editor.draftHint")}</p>
          )}
          <PsdDropzone onFile={handleFile} busy={parsing} />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Metadata bar */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 p-3 rounded-xl border bg-card/40">
            <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <Label htmlFor="template-name" className="text-foreground text-xs">
                  {t("fields.namePtBr")}
                </Label>
                <Input
                  id="template-name"
                  value={template.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="mt-1"
                />
                <p className="text-[11px] text-muted-foreground mt-1 font-mono truncate">
                  "id": "{template.id}"
                </p>
              </div>
              <div className="min-w-0">
                <Label htmlFor="template-name-en" className="text-foreground text-xs">
                  {t("fields.nameEn")}
                </Label>
                <Input
                  id="template-name-en"
                  value={nameEn}
                  onChange={(e) => setNameEn(e.target.value)}
                  placeholder={t("fields.nameEnPlaceholder")}
                  className="mt-1"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  {t("fields.nameEnHint")}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <FileImage className="w-4 h-4" />
                {template.width} × {template.height}
              </span>
              <span className="flex items-center gap-1.5">
                <Target className="w-4 h-4 text-primary" />
                {t("fields.slotsCount", { count: uniqueSlotCount })}
              </span>
              <span className="flex items-center gap-1.5">
                <ImageIcon className={`w-4 h-4 ${previewBlob ? "text-primary" : ""}`} />
                {previewBlob ? previewFileName : "—"}
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 p-3 rounded-xl border bg-card/40">
            <div className="min-w-0">
              <Label htmlFor="template-is-free" className="text-sm font-medium">
                {t("free.title")}
              </Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {isFree ? t("free.hintFree") : t("free.hintPaid")}
              </p>
            </div>
            <Switch
              id="template-is-free"
              checked={isFree}
              onCheckedChange={setIsFree}
              aria-label={t("free.title")}
            />
          </div>

          {backendId && (
            <CategoriesPanel
              templateId={backendId}
              categories={backendRecord?.categories ?? []}
            />
          )}

          {/* 3-column workspace */}
          <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)_340px] gap-4">
            <section className="space-y-2 min-w-0">
              <SectionHeader title={t("layers.title")} hint={t("layers.hint")} />
              <div
                className="space-y-1.5 overflow-y-auto pr-1"
                style={{ maxHeight: canvasMaxHeight }}
              >
                {[...template.layers]
                  .sort((a, b) => b.z - a.z)
                  .map((layer) => (
                    <LayerRow
                      key={layer.z}
                      layer={layer}
                      thumbnail={
                        layer.type === "image"
                          ? parsed?.thumbnails.get(layer.imageUrl)
                          : undefined
                      }
                      onToggleSlot={() => handleToggleSlot(layer.z)}
                      onFocus={() => setActiveZ(layer.z === activeZ ? null : layer.z)}
                      active={layer.z === activeZ}
                    />
                  ))}
              </div>
            </section>

            <section className="min-w-0">
              <PreviewCanvas
                template={template}
                canvases={parsed?.canvases ?? new Map()}
                slotOverrides={slotOverrideImages}
                highlightedLayerZ={activeZ}
                maxHeight={canvasMaxHeight}
              />
            </section>

            <section className="space-y-4 min-w-0">
              <div className="overflow-y-auto pr-1" style={{ maxHeight: canvasMaxHeight }}>
                <SlotsPanel
                  slots={slotLayers}
                  overrideUrls={slotOverrideUrls}
                  onUpload={handleSlotUpload}
                  onClear={handleSlotClear}
                />
                <div className="border-t my-4" />
                <PreviewImagePanel
                  previewUrl={previewUrl ?? undefined}
                  fileName={previewFileName}
                  onCapture={handleCapturePreview}
                  onUpload={handlePreviewUpload}
                  onClear={clearPreview}
                  capturing={capturing}
                />
              </div>
            </section>
          </div>
        </div>
      )}

      <SaveProgress step={step} open={isSaving} />
    </PageContent>
  );
}

function SectionHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      {hint && <span className="text-[11px] text-muted-foreground truncate">{hint}</span>}
    </div>
  );
}
